/*!
 * @pixi/spritesheet - v7.0.0-alpha
 * Compiled Fri, 09 Sep 2022 16:09:18 UTC
 *
 * @pixi/spritesheet is licensed under the MIT License.
 * http://www.opensource.org/licenses/mit-license
 */
import { LoaderParserPriority } from '@pixi/assets';
import { Texture, BaseTexture, utils, Rectangle, ExtensionType, extensions, settings } from '@pixi/core';

const _Spritesheet = class {
  constructor(texture, data, resolutionFilename = null) {
    this.linkedSheets = [];
    this._texture = texture instanceof Texture ? texture : null;
    this.baseTexture = texture instanceof BaseTexture ? texture : this._texture.baseTexture;
    this.textures = {};
    this.animations = {};
    this.data = data;
    const resource = this.baseTexture.resource;
    this.resolution = this._updateResolution(resolutionFilename || (resource ? resource.url : null));
    this._frames = this.data.frames;
    this._frameKeys = Object.keys(this._frames);
    this._batchIndex = 0;
    this._callback = null;
  }
  _updateResolution(resolutionFilename = null) {
    const { scale } = this.data.meta;
    let resolution = utils.getResolutionOfUrl(resolutionFilename, null);
    if (resolution === null) {
      resolution = parseFloat(scale ?? "1");
    }
    if (resolution !== 1) {
      this.baseTexture.setResolution(resolution);
    }
    return resolution;
  }
  parse() {
    return new Promise((resolve) => {
      this._callback = resolve;
      this._batchIndex = 0;
      if (this._frameKeys.length <= _Spritesheet.BATCH_SIZE) {
        this._processFrames(0);
        this._processAnimations();
        this._parseComplete();
      } else {
        this._nextBatch();
      }
    });
  }
  _processFrames(initialFrameIndex) {
    let frameIndex = initialFrameIndex;
    const maxFrames = _Spritesheet.BATCH_SIZE;
    while (frameIndex - initialFrameIndex < maxFrames && frameIndex < this._frameKeys.length) {
      const i = this._frameKeys[frameIndex];
      const data = this._frames[i];
      const rect = data.frame;
      if (rect) {
        let frame = null;
        let trim = null;
        const sourceSize = data.trimmed !== false && data.sourceSize ? data.sourceSize : data.frame;
        const orig = new Rectangle(0, 0, Math.floor(sourceSize.w) / this.resolution, Math.floor(sourceSize.h) / this.resolution);
        if (data.rotated) {
          frame = new Rectangle(Math.floor(rect.x) / this.resolution, Math.floor(rect.y) / this.resolution, Math.floor(rect.h) / this.resolution, Math.floor(rect.w) / this.resolution);
        } else {
          frame = new Rectangle(Math.floor(rect.x) / this.resolution, Math.floor(rect.y) / this.resolution, Math.floor(rect.w) / this.resolution, Math.floor(rect.h) / this.resolution);
        }
        if (data.trimmed !== false && data.spriteSourceSize) {
          trim = new Rectangle(Math.floor(data.spriteSourceSize.x) / this.resolution, Math.floor(data.spriteSourceSize.y) / this.resolution, Math.floor(rect.w) / this.resolution, Math.floor(rect.h) / this.resolution);
        }
        this.textures[i] = new Texture(this.baseTexture, frame, orig, trim, data.rotated ? 2 : 0, data.anchor);
        Texture.addToCache(this.textures[i], i);
      }
      frameIndex++;
    }
  }
  _processAnimations() {
    const animations = this.data.animations || {};
    for (const animName in animations) {
      this.animations[animName] = [];
      for (let i = 0; i < animations[animName].length; i++) {
        const frameName = animations[animName][i];
        this.animations[animName].push(this.textures[frameName]);
      }
    }
  }
  _parseComplete() {
    const callback = this._callback;
    this._callback = null;
    this._batchIndex = 0;
    callback.call(this, this.textures);
  }
  _nextBatch() {
    this._processFrames(this._batchIndex * _Spritesheet.BATCH_SIZE);
    this._batchIndex++;
    setTimeout(() => {
      if (this._batchIndex * _Spritesheet.BATCH_SIZE < this._frameKeys.length) {
        this._nextBatch();
      } else {
        this._processAnimations();
        this._parseComplete();
      }
    }, 0);
  }
  destroy(destroyBase = false) {
    for (const i in this.textures) {
      this.textures[i].destroy();
    }
    this._frames = null;
    this._frameKeys = null;
    this.data = null;
    this.textures = null;
    if (destroyBase) {
      this._texture?.destroy();
      this.baseTexture.destroy();
    }
    this._texture = null;
    this.baseTexture = null;
    this.linkedSheets = [];
  }
};
let Spritesheet = _Spritesheet;
Spritesheet.BATCH_SIZE = 1e3;

const loadSpritesheet = {
  extension: {
    type: ExtensionType.LoadParser,
    priority: LoaderParserPriority.Normal
  },
  async testParse(asset, options) {
    return utils.path.extname(options.src).includes(".json") && !!asset.frames;
  },
  async parse(asset, options, loader) {
    let basePath = utils.path.dirname(options.src);
    if (basePath && basePath.lastIndexOf("/") !== basePath.length - 1) {
      basePath += "/";
    }
    const imagePath = basePath + asset.meta.image;
    const assets = await loader.load([imagePath]);
    const texture = assets[imagePath];
    const spritesheet = new Spritesheet(texture.baseTexture, asset, options.src);
    await spritesheet.parse();
    const multiPacks = asset?.meta?.related_multi_packs;
    if (Array.isArray(multiPacks)) {
      const promises = [];
      for (const item of multiPacks) {
        if (typeof item !== "string") {
          continue;
        }
        const itemUrl = basePath + item;
        if (options.data?.ignoreMultiPack) {
          continue;
        }
        promises.push(loader.load({
          src: itemUrl,
          data: {
            ignoreMultiPack: true
          }
        }));
      }
      const res = await Promise.all(promises);
      spritesheet.linkedSheets = res;
      res.forEach((item) => {
        item.linkedSheets = [spritesheet].concat(spritesheet.linkedSheets.filter((sp) => sp !== item));
      });
    }
    return spritesheet;
  },
  unload(spritesheet) {
    spritesheet.destroy(true);
  }
};
extensions.add(loadSpritesheet);

function getCacheableAssets(keys, asset, ignoreMultiPack) {
  const out = {};
  keys.forEach((key) => {
    out[key] = asset;
  });
  Object.keys(asset.textures).forEach((key) => {
    out[key] = asset.textures[key];
  });
  if (!ignoreMultiPack) {
    const basePath = utils.path.dirname(keys[0]);
    asset.linkedSheets.forEach((item, i) => {
      const out2 = getCacheableAssets([`${basePath}/${asset.data.meta.related_multi_packs[i]}`], item, true);
      Object.assign(out, out2);
    });
  }
  return out;
}
const cacheSpritesheet = {
  extension: ExtensionType.CacheParser,
  test: (asset) => asset instanceof Spritesheet,
  getCacheableAssets: (keys, asset) => getCacheableAssets(keys, asset, false)
};
extensions.add(cacheSpritesheet);

const validImages = ["jpg", "png", "jpeg", "avif", "webp"];
const resolveSpriteSheetUrl = {
  extension: ExtensionType.ResolveParser,
  test: (value) => {
    const tempURL = value.split("?")[0];
    const split = tempURL.split(".");
    const extension = split.pop();
    const format = split.pop();
    return extension === "json" && validImages.includes(format);
  },
  parse: (value) => {
    const split = value.split(".");
    return {
      resolution: parseFloat(settings.RETINA_PREFIX.exec(value)?.[1] ?? "1"),
      format: split[split.length - 2],
      src: value
    };
  }
};
extensions.add(resolveSpriteSheetUrl);

export { Spritesheet, cacheSpritesheet, loadSpritesheet, resolveSpriteSheetUrl };
//# sourceMappingURL=spritesheet.mjs.map
